import { apiRequest } from '@/api/client';
import { pushWarning } from '@/lib/warningsBuffer';
import { EMERGENCY_CACHE, withoutQuery } from '@/playback/emergency-cache';
import type { Layout, Playlist, ZoneBindingMedia } from '@/lib/types';

/**
 * The screen's emergency content, held so that nothing has to be fetched when
 * it is needed.
 *
 * Everything else in the player degrades gracefully: a failed timeline fetch
 * keeps the last one, a failed media load skips an item, a programme zone with
 * no payload renders empty. Emergency content cannot degrade. The situation it
 * exists for — smoke, an evacuation, a building whose network is part of what
 * just failed — is the situation in which every one of those fallbacks is
 * already exhausted, so it is fetched in advance, at pairing and whenever the
 * configuration changes, and kept in two places:
 *
 *  - **The playlist and its layouts** as a snapshot in `localStorage`, because
 *    the JSON cache is a 24-hour NetworkFirst store and a screen can be offline
 *    for longer than that. `playback/playlist.ts` falls back to this snapshot
 *    when a fetch fails, so an emergency raised on the third offline day still
 *    resolves its content.
 *  - **The media bytes** in a Cache Storage bucket the service worker checks
 *    before the ordinary media cache and nothing ever evicts from (see `sw.ts`).
 *
 * Pinning is keyed on the URL without its query string: presigned GETs rotate
 * hourly, and re-downloading an evacuation video twenty-four times a day to
 * store the same bytes under a new signature would be its own kind of failure.
 */

const SNAPSHOT_KEY = 'l42.player.emergency.v1';

interface EmergencySnapshot {
  playlistId: string;
  pinnedAt: string;
  playlist: Playlist;
  /** Keyed by layout id. */
  layouts: Record<string, Layout>;
  /** Media URLs pinned for this playlist, signature stripped. */
  mediaKeys: string[];
}

function readSnapshot(): EmergencySnapshot | null {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as EmergencySnapshot;
  } catch {
    return null;
  }
}

function writeSnapshot(snapshot: EmergencySnapshot): void {
  try {
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch (err) {
    // Quota, or a locked-down kiosk profile. The media is still pinned and the
    // JSON cache still holds a copy for the next 24 hours; this is a narrowing
    // of the guarantee, not a loss of it, and it has to be visible in telemetry
    // rather than thrown away.
    pushWarning(`emergency-snapshot-store-failed:${(err as Error).message ?? 'err'}`);
  }
}

function clearSnapshot(): void {
  try {
    localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}

/** The pinned playlist, if it is the one asked for. */
export function pinnedPlaylist(playlistId: string): Playlist | null {
  const snapshot = readSnapshot();
  if (!snapshot || snapshot.playlist.id !== playlistId) return null;
  return snapshot.playlist;
}

/** A pinned layout, whichever emergency playlist it belongs to. */
export function pinnedLayout(layoutId: string): Layout | null {
  return readSnapshot()?.layouts[layoutId] ?? null;
}

export function pinnedPlaylistId(): string | null {
  return readSnapshot()?.playlistId ?? null;
}

function mediaUrlsOf(playlist: Playlist): string[] {
  const urls = new Set<string>();
  for (const item of playlist.items) {
    for (const binding of Object.values(item.zones)) {
      if (binding.kind !== 'media') continue;
      const url = (binding as ZoneBindingMedia).downloadUrl;
      if (url) urls.add(url);
    }
  }
  return [...urls];
}

function cacheStorage(): CacheStorage | null {
  // Absent in tests, and in any browser context without a secure origin. The
  // snapshot half of pinning still works there.
  return typeof caches === 'undefined' ? null : caches;
}

/**
 * Fetches and pins the emergency playlist, its layouts and its media.
 *
 * Safe to call repeatedly: media already pinned is not re-downloaded, and media
 * belonging to a playlist that is no longer the emergency one is dropped, so a
 * house that swaps its evacuation video does not carry the old one forever.
 *
 * Never throws. A screen whose emergency content cannot be pinned is a screen
 * that must still play tonight's programme.
 */
export async function pinEmergencyContent(playlistId: string | null): Promise<void> {
  if (playlistId === null) {
    // No emergency content configured for this screen. Drop what we hold: an
    // operator who removed the designation did so deliberately, and a screen
    // rendering last quarter's evacuation notice would be worse than one
    // rendering none.
    clearSnapshot();
    await dropPinnedMedia(new Set());
    return;
  }
  try {
    const playlist = await apiRequest<Playlist>(`/player/playlists/${playlistId}`);
    const layoutIds = [...new Set(playlist.items.map((i) => i.layoutId))];
    const layouts: Record<string, Layout> = {};
    for (const layoutId of layoutIds) {
      layouts[layoutId] = await apiRequest<Layout>(`/player/layouts/${layoutId}`);
    }
    const mediaUrls = mediaUrlsOf(playlist);
    writeSnapshot({
      playlistId,
      pinnedAt: new Date().toISOString(),
      playlist,
      layouts,
      mediaKeys: mediaUrls.map(withoutQuery),
    });
    await pinMedia(mediaUrls);
  } catch (err) {
    pushWarning(`emergency-pin-failed:${playlistId}:${(err as Error).message ?? 'err'}`);
  }
}

async function pinMedia(urls: readonly string[]): Promise<void> {
  const store = cacheStorage();
  if (!store) return;
  const cache = await store.open(EMERGENCY_CACHE);
  const wanted = new Set(urls.map(withoutQuery));
  for (const url of urls) {
    try {
      const existing = await cache.match(url, { ignoreSearch: true });
      if (existing) continue;
      // `no-cors` because that is how an `<img>` or `<video>` will request it,
      // and an opaque response is what the media cache stores for those anyway.
      const res = await fetch(url, { mode: 'no-cors' });
      await cache.put(url, res);
    } catch (err) {
      // One unreachable asset must not cost us the ones that are reachable.
      pushWarning(`emergency-pin-media-failed:${(err as Error).message ?? 'err'}`);
    }
  }
  await dropPinnedMedia(wanted);
}

/** Removes pinned entries outside `wanted`, compared without the signature. */
async function dropPinnedMedia(wanted: ReadonlySet<string>): Promise<void> {
  const store = cacheStorage();
  if (!store) return;
  try {
    if (!(await store.has(EMERGENCY_CACHE))) return;
    const cache = await store.open(EMERGENCY_CACHE);
    for (const request of await cache.keys()) {
      if (!wanted.has(withoutQuery(request.url))) await cache.delete(request);
    }
  } catch (err) {
    pushWarning(`emergency-unpin-failed:${(err as Error).message ?? 'err'}`);
  }
}
